import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart2,
  Boxes,
  CheckCircle,
  ClipboardList,
  FileText,
  FolderOpen,
  LayoutDashboard,
  LogOut,
  RefreshCw,
  Search,
  Settings,
  Truck,
  UserRound,
  MessageSquare,
} from 'lucide-react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { clearLocalSession, redirectToUnifiedAuth } from '../../utils/unifiedAuth';
import ApprovalPage from '../Approval/ApprovalPage';
import BasketsPage from '../Baskets/BasketsPage';
import EstimatesPage from '../Estimates/EstimatesPage';
import SettingsPage from '../Settings/SettingsPage';
import ShippingPage from '../Shipping/ShippingPage';
import StatisticsPage from '../Statistics/StatisticsPage';
import UserProfile from '../Profile/UserProfile';
import ClientTicketQueue from '../ClientTickets/ClientTicketQueue';
import ClientPickupRequestsPage from '../ClientPickups/ClientPickupRequestsPage';
import ClientTicketNotifier from '../ClientTicketNotifier/ClientTicketNotifier';
import useClientTicketsRealtime from '../../hooks/useClientTicketsRealtime';
import useApprovalsRealtime from '../../hooks/useApprovalsRealtime';
import styles from './ProjectSelectionPage.module.css';
import commonStyles from '../../styles/common.module.css';

const EMPTY_METRICS = {
  activeRequests: 0,
  approvals: 0,
};

const unwrapCount = (data) => {
  if (Array.isArray(data)) return data.length;
  if (typeof data?.count === 'number') return data.count;
  return data?.results?.length || 0;
};

const getClientProjectName = (project) => (
  project?.client_display_name || project?.client_name || 'Заявки на ремонт'
);

const getClientProjectDescription = (project) => (
  project?.client_display_description || project?.client_description || ''
);

const ProjectSelectionPage = () => {
  const [projects, setProjects] = useState([]);
  const [projectMetrics, setProjectMetrics] = useState({});
  const [ticketCounts, setTicketCounts] = useState({});
  const [pickupCount, setPickupCount] = useState(0);
  const [isProjectsLoading, setIsProjectsLoading] = useState(true);
  const [isMetricsLoading, setIsMetricsLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeSection, setActiveSection] = useState('projects');
  const [ticketOpenRequest, setTicketOpenRequest] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const { user, refreshUser, loading } = useAuth();
  const metricsAbortRef = useRef(null);

  const roleNames = useMemo(() => (user?.roles || []).map(role => role.name), [user]);
  const permissionKeys = useMemo(() => new Set(user?.permissions || []), [user]);
  const hasConfiguredPermissions = Array.isArray(user?.permissions);
  const can = useCallback((permission, legacyValue) => (
    hasConfiguredPermissions
      ? permissionKeys.has(permission) || permissionKeys.has('crm.admin.override')
      : legacyValue
  ), [hasConfiguredPermissions, permissionKeys]);
  const isAdmin = roleNames.includes('admin');
  const isAssistant = roleNames.includes('Ассистент');
  const isGuest = roleNames.includes('Гость');
  const isLogist = roleNames.includes('Специалист по логистике');
  const canSeeShipments = can('crm.page.shipments', isAdmin || isLogist);
  const canSeePickups = can('crm.page.client_pickups', isAdmin || isLogist);
  const canSeeApprovals = can('crm.page.approvals', isAdmin || isAssistant);
  const canSeeTickets = can('crm.page.tickets', isAdmin || isAssistant || isLogist || isGuest);
  const canSeeEstimates = can('crm.page.estimates', !isGuest && (isAdmin || isAssistant));
  const canSeeBaskets = can('crm.page.baskets', !isGuest);
  const canSeeStatistics = can('crm.page.statistics', !isGuest);
  const canManageSettings = can('crm.admin.settings', isAdmin);
  const approvalsBadge = useMemo(() => (
    Object.values(projectMetrics).reduce((sum, metrics) => sum + Number(metrics?.approvals || 0), 0)
  ), [projectMetrics]);

  const fetchProjectMetrics = useCallback(async (projectList) => {
    if (!projectList.length) {
      metricsAbortRef.current?.abort();
      setProjectMetrics({});
      setIsMetricsLoading(false);
      return;
    }

    metricsAbortRef.current?.abort();
    const controller = new AbortController();
    metricsAbortRef.current = controller;
    setIsMetricsLoading(true);
    setProjectMetrics({});

    try {
      const response = await api.get('/repairs/repair-requests/project_metrics/', {
        signal: controller.signal,
      });
      if (controller.signal.aborted) {
        return;
      }
      const serverMetrics = response.data || {};
      const metricsByProject = projectList.reduce((acc, project) => {
        acc[project.id] = {
          ...EMPTY_METRICS,
          ...(serverMetrics[String(project.id)] || serverMetrics[project.id] || {}),
        };
        return acc;
      }, {});
      setProjectMetrics(metricsByProject);
    } catch (err) {
      if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') {
        return;
      }
      console.error('Ошибка при загрузке показателей проектов:', err);
    } finally {
      if (metricsAbortRef.current === controller) {
        metricsAbortRef.current = null;
      }
      if (!controller.signal.aborted) {
        setIsMetricsLoading(false);
      }
    }
  }, []);

  useEffect(() => () => {
    metricsAbortRef.current?.abort();
  }, []);

  const fetchTicketCounts = useCallback(async () => {
    if (!canSeeTickets) {
      setTicketCounts({});
      return;
    }
    try {
      const response = await api.get('/repairs/client-tickets/counts/');
      setTicketCounts(response.data || {});
    } catch (err) {
      console.error('Ошибка при загрузке счетчика тикетов:', err);
    }
  }, [canSeeTickets]);

  const fetchPickupCount = useCallback(async () => {
    if (!canSeeShipments) {
      setPickupCount(0);
      return;
    }
    try {
      const response = await api.get('/repairs/client-requests/pickup-requests/', {
        params: {
          pickup_status: 'requested',
          page_size: 1,
        },
      });
      setPickupCount(response.data?.count || unwrapCount(response.data));
    } catch (err) {
      console.error('Ошибка при загрузке счетчика клиентских заборов:', err);
    }
  }, [canSeeShipments]);

  useEffect(() => {
    fetchTicketCounts();
  }, [fetchTicketCounts]);

  const handleTicketsRealtime = useCallback((payload) => {
    fetchTicketCounts();
    fetchPickupCount();
    if (String(payload?.action || '').startsWith('client_approval') || String(payload?.action || '').startsWith('approval_state')) {
      fetchProjectMetrics(projects);
    }
  }, [fetchPickupCount, fetchProjectMetrics, fetchTicketCounts, projects]);

  useClientTicketsRealtime(canSeeTickets ? handleTicketsRealtime : null);

  const handleApprovalsRealtime = useCallback(() => {
    fetchProjectMetrics(projects);
  }, [fetchProjectMetrics, projects]);

  useApprovalsRealtime(
    canSeeApprovals ? null : undefined,
    canSeeApprovals ? handleApprovalsRealtime : null
  );

  useEffect(() => {
    fetchPickupCount();
  }, [fetchPickupCount]);

  const openTicketSection = useCallback((ticketId) => {
    if (!ticketId) return;
    setActiveSection('client-tickets');
    setTicketOpenRequest({ id: Number(ticketId), nonce: Date.now() });
  }, []);

  const openDocumentSection = useCallback((requestId) => {
    if (requestId) {
      localStorage.setItem('client_ticket_open_documents_request_id', requestId);
    }
    setActiveSection('client-tickets');
  }, []);

  const openPickupSection = useCallback((requestId) => {
    setActiveSection('client-pickups');
    if (requestId) {
      localStorage.setItem('client_pickup_open_id', requestId);
    }
  }, []);

  useEffect(() => {
    const pendingTicketId = localStorage.getItem('client_ticket_open_id');
    if (pendingTicketId) {
      localStorage.removeItem('client_ticket_open_id');
      openTicketSection(pendingTicketId);
    }
  }, [openTicketSection]);

  useEffect(() => {
    const pendingRequestId = localStorage.getItem('client_ticket_open_documents_request_id');
    if (pendingRequestId) {
      openDocumentSection(pendingRequestId);
    }
  }, [openDocumentSection]);

  useEffect(() => {
    const pendingSection = localStorage.getItem('project_selection_open_section');
    if (pendingSection) {
      localStorage.removeItem('project_selection_open_section');
      setActiveSection(pendingSection);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ticketId = params.get('ticket_id');
    const section = params.get('section');
    if (ticketId) {
      openTicketSection(ticketId);
    } else if (section === 'client-pickups') {
      openPickupSection(params.get('request_id'));
    } else if (section === 'approvals') {
      setActiveSection('approvals');
    }
  }, [openPickupSection, openTicketSection]);

  useEffect(() => {
    const pendingPickupId = localStorage.getItem('client_pickup_open_id');
    if (pendingPickupId) {
      openPickupSection(pendingPickupId);
    }
  }, [openPickupSection]);

  useEffect(() => {
    const handleOpenPickup = (event) => {
      const requestId = event?.detail?.repair_request_id || event?.detail?.repairRequestId;
      openPickupSection(requestId);
    };
    window.addEventListener('crm-open-client-pickup', handleOpenPickup);
    return () => window.removeEventListener('crm-open-client-pickup', handleOpenPickup);
  }, [openPickupSection]);

  useEffect(() => {
    const handleOpenTicket = (event) => {
      const ticketId = event?.detail?.ticket_id || event?.detail?.ticketId;
      openTicketSection(ticketId);
    };
    window.addEventListener('crm-open-client-ticket', handleOpenTicket);
    return () => window.removeEventListener('crm-open-client-ticket', handleOpenTicket);
  }, [openTicketSection]);

  useEffect(() => {
    const handleOpenDocuments = (event) => {
      const requestId = event?.detail?.repair_request_id || event?.detail?.repairRequestId;
      openDocumentSection(requestId);
    };
    window.addEventListener('crm-open-client-documents', handleOpenDocuments);
    return () => window.removeEventListener('crm-open-client-documents', handleOpenDocuments);
  }, [openDocumentSection]);

  useEffect(() => {
    const handleOpenProjectSection = (event) => {
      const section = event?.detail?.section;
      if (section) setActiveSection(section);
    };
    window.addEventListener('crm-open-project-section', handleOpenProjectSection);
    return () => window.removeEventListener('crm-open-project-section', handleOpenProjectSection);
  }, []);

  const fetchProjects = useCallback(async () => {
    if (!user) return;
    try {
      setIsProjectsLoading(true);
      setError('');
      let availableProjects = [];
      if (isGuest) {
        const response = await api.get('/repairs/client-requests/projects/');
        availableProjects = Array.isArray(response.data) ? response.data : [];
      } else if (Array.isArray(user.projects) && user.projects.length > 0) {
        availableProjects = user.projects;
      } else {
        const response = await api.get('/core/projects/');
        const allProjects = Array.isArray(response.data) ? response.data : [];
        const userProjectIds = user?.projects?.map(p => p.id) || [];
        availableProjects = userProjectIds.length > 0
          ? allProjects.filter(project => userProjectIds.includes(project.id))
          : allProjects;
      }
      setProjects(availableProjects);
      setIsProjectsLoading(false);
      fetchProjectMetrics(availableProjects);
    } catch (err) {
      if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') {
        return;
      }
      console.error('Ошибка при получении проектов:', err);
      setError('Не удалось загрузить проекты. Пожалуйста, попробуйте позже.');
    } finally {
      setIsProjectsLoading(false);
    }
  }, [fetchProjectMetrics, isGuest, user]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const menuItems = useMemo(() => ([
    {
      id: 'projects',
      label: isGuest ? 'Заявки' : 'Проекты',
      icon: FolderOpen,
      visible: can('crm.page.projects', true),
    },
    {
      id: 'shipments',
      label: 'Отправки',
      icon: Truck,
      visible: canSeeShipments,
    },
    {
      id: 'client-pickups',
      label: 'Забор заявок',
      icon: Truck,
      visible: canSeePickups,
      badge: pickupCount,
    },
    {
      id: 'approvals',
      label: 'Согласования',
      icon: CheckCircle,
      visible: canSeeApprovals,
      badge: approvalsBadge,
    },
    {
      id: 'client-tickets',
      label: 'Тикеты',
      icon: MessageSquare,
      visible: canSeeTickets,
      badge: ticketCounts.needs_response || 0,
    },
    {
      id: 'estimates',
      label: 'Сметы',
      icon: FileText,
      visible: canSeeEstimates,
    },
    {
      id: 'baskets',
      label: 'Корзины',
      icon: Boxes,
      visible: canSeeBaskets,
    },
    {
      id: 'statistics',
      label: 'Статистика',
      icon: BarChart2,
      visible: canSeeStatistics,
    },
    {
      id: 'settings',
      label: 'Настройки',
      icon: Settings,
      visible: canManageSettings,
    },
    {
      id: 'profile',
      label: 'Личный кабинет',
      icon: UserRound,
      visible: can('crm.page.profile', true),
    },
  ]).filter(item => item.visible), [approvalsBadge, can, canManageSettings, canSeeApprovals, canSeeBaskets, canSeeEstimates, canSeePickups, canSeeShipments, canSeeStatistics, canSeeTickets, isGuest, pickupCount, ticketCounts.needs_response]);

  useEffect(() => {
    if (!menuItems.some(item => item.id === activeSection)) {
      setActiveSection('projects');
    }
  }, [activeSection, menuItems]);

  const handleProjectSelect = async (project) => {
    metricsAbortRef.current?.abort();
    setIsMetricsLoading(false);
    localStorage.setItem('selected_project_id', project.id);
    localStorage.setItem('selected_project_name', isGuest ? getClientProjectName(project) : project.name);
    await refreshUser();
    navigate(isGuest ? '/client' : '/');
  };

  const handleLogout = () => {
    clearLocalSession();
    redirectToUnifiedAuth('/');
  };

  const filteredProjects = projects.filter((project) => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return true;
    const displayName = isGuest ? getClientProjectName(project) : project.name;
    const displayDescription = isGuest ? getClientProjectDescription(project) : project.description;
    return (
      displayName?.toLowerCase().includes(term) ||
      displayDescription?.toLowerCase().includes(term)
    );
  });

  const totals = projects.reduce((acc, project) => {
    const metrics = projectMetrics[project.id] || EMPTY_METRICS;
    acc.activeRequests += metrics.activeRequests;
    acc.approvals += metrics.approvals;
    return acc;
  }, { activeRequests: 0, approvals: 0 });

  const renderProjects = () => {
    if (isProjectsLoading) {
      return (
        <div className={styles.loadingContainer}>
          <div className={styles.loadingContent}>
            <div className={styles.spinner}></div>
            <p className={styles.loadingText}>Загрузка проектов...</p>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className={styles.errorContainer}>
          <h3 className={styles.errorTitle}>{error}</h3>
          <button
            type="button"
            className={styles.retryButton}
            onClick={fetchProjects}
          >
            Повторить
          </button>
        </div>
      );
    }

    if (!projects.length) {
      return (
        <div className={styles.emptyState}>
          <ClipboardList size={44} />
          <h3>{isGuest ? 'Разделы заявок не найдены' : 'Проекты не найдены'}</h3>
          <p>{isGuest ? 'Нет доступных заявок для отображения' : 'Нет доступных проектов для отображения'}</p>
        </div>
      );
    }

    return (
      <>
        <div className={styles.projectToolbar}>
          <div className={styles.searchBox}>
            <Search size={18} />
            <input
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder={isGuest ? 'Поиск по разделу заявок' : 'Поиск по проекту или описанию'}
            />
          </div>
          <button
            type="button"
            className={styles.refreshButton}
            onClick={() => fetchProjectMetrics(projects)}
            disabled={isMetricsLoading}
          >
            <RefreshCw size={18} />
            Обновить
          </button>
        </div>

        <div className={styles.summaryStrip}>
          <div>
            <span>Текущие заявки</span>
            <strong>{totals.activeRequests}</strong>
          </div>
          {canSeeApprovals && (
            <div>
              <span>Согласования</span>
              <strong>{totals.approvals}</strong>
            </div>
          )}
        </div>

        <div className={styles.tableShell}>
          <table className={styles.projectTable}>
            <thead>
              <tr>
                <th>{isGuest ? 'Раздел заявок' : 'Проект'}</th>
                <th>{isGuest ? 'Описание для клиента' : 'Описание'}</th>
                <th>Текущие заявки</th>
                {canSeeApprovals && <th>Согласования</th>}
              </tr>
            </thead>
            <tbody>
              {filteredProjects.map((project) => {
                const metrics = projectMetrics[project.id] || EMPTY_METRICS;
                const isCurrent = String(localStorage.getItem('selected_project_id')) === String(project.id);
                return (
                  <tr key={project.id} onClick={() => handleProjectSelect(project)}>
                    <td>
                      <div className={styles.projectNameCell}>
                        <span>{isGuest ? getClientProjectName(project) : project.name}</span>
                        {isCurrent && <em>Текущий</em>}
                      </div>
                    </td>
                    <td>{isGuest ? (getClientProjectDescription(project) || '-') : (project.description || '-')}</td>
                    <td>{metrics.activeRequests}</td>
                    {canSeeApprovals && <td>{metrics.approvals}</td>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </>
    );
  };

  const renderActiveSection = () => {
    switch (activeSection) {
      case 'shipments':
        return <ShippingPage allProjects embedded hideModeToggle />;
      case 'client-pickups':
        return <ClientPickupRequestsPage />;
      case 'approvals':
        return <ApprovalPage allProjects embedded projects={projects} />;
      case 'client-tickets':
        return (
          <ClientTicketQueue
            embedded
            initialTicketId={ticketOpenRequest?.id}
            initialTicketNonce={ticketOpenRequest?.nonce}
          />
        );
      case 'estimates':
        return <EstimatesPage embedded />;
      case 'baskets':
        return <BasketsPage embedded allProjects />;
      case 'statistics':
        return <StatisticsPage embedded />;
      case 'settings':
        return <SettingsPage embedded />;
      case 'profile':
        return <UserProfile embedded />;
      case 'projects':
      default:
        return renderProjects();
    }
  };

  if (loading) {
    return (
      <div className={`${commonStyles.pageContainer} ${styles.loadingContainer}`}>
        <div className={styles.loadingContent}>
          <div className={styles.spinner}></div>
          <p className={styles.loadingText}>Загрузка данных...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    redirectToUnifiedAuth('/select-project');
    return null;
  }

  return (
    <div className={`${commonStyles.fadeIn} ${styles.workspace}`}>
      <aside className={styles.sidebar}>
        <div className={styles.brandBlock}>
          <LayoutDashboard size={24} />
          <div>
            <strong>CRM</strong>
            <span>{user.full_name || user.username}</span>
          </div>
          <ClientTicketNotifier />
        </div>

        <nav className={styles.navList}>
          {menuItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                type="button"
                className={`${styles.navButton} ${activeSection === item.id ? styles.active : ''}`}
                onClick={() => setActiveSection(item.id)}
              >
                <Icon size={20} />
                <span className={styles.navLabel}>{item.label}</span>
                {Number(item.badge || 0) > 0 && <em className={styles.navBadge}>{item.badge}</em>}
              </button>
            );
          })}
        </nav>

        <button type="button" className={styles.logoutButton} onClick={handleLogout}>
          <LogOut size={20} />
          <span>Выйти</span>
        </button>
      </aside>

      <main className={styles.mainArea}>
        <section className={styles.contentArea}>
          {renderActiveSection()}
        </section>
      </main>
    </div>
  );
};

export default ProjectSelectionPage;
